import React from 'react';
import { type SimpleCardSize } from '@naf-simba/sections-cards';
import { NavCard } from '@components/simba-cards';
import { AdvancedImage } from '@cloudinary/react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { nafColor } from '@nafcore/theme';
import { getNavCardVariant } from '@lib/getNavCardVariant';
import { useCloudinary } from '../../../hooks/Cloudinary/useCloudinary';
import { MainEntryType } from '../../../../../types/siteStructureType';
import { useDocumentUrlWithNode } from '../../../hooks/Documents/useDocumentUrl';

type Props = {
  block: MainEntryType;
  size?: SimpleCardSize;
};

export const SingleNavigationCardBlock: React.FC<Props> = ({ block, size }) => {
  const { title, illustration, internalLink, externalLink, colorVariant, description } = block;
  const { getImage } = useCloudinary();
  const { variant, color } = getNavCardVariant(colorVariant);

  const itemImage = illustration?.publicId && getImage(illustration.publicId, 'svg');

  return (
    <LinkComponent externalLink={externalLink} internalLink={internalLink}>
      <StyledSimpleCard
        title={title || internalLink?.name || ''}
        description={description}
        illustration={itemImage ? <AdvancedImage cldImg={itemImage} alt={illustration?.altText || ''} /> : null}
        size={size ?? 'medium'}
        height="100%"
        variant={variant}
        color={color}
        isInternalLink
      />
    </LinkComponent>
  );
};

type LinkComponentProps = Pick<MainEntryType, 'externalLink' | 'internalLink'> & { children: React.ReactNode };

const LinkComponent = ({ externalLink, internalLink, children }: LinkComponentProps) => {
  const url = useDocumentUrlWithNode(internalLink) || '';

  if (url) {
    return <StyledLink to={url}>{children}</StyledLink>;
  }
  if (externalLink) {
    const { blank, href } = externalLink;
    return (
      <StyledA href={href} target={blank ? '_blank' : '_self'} rel={blank ? 'noreferrer' : ''}>
        {children}
      </StyledA>
    );
  }
  return <PlainWrapper>{children}</PlainWrapper>;
};

const StyledSimpleCard = styled(NavCard)`
  width: 100%;
  height: 100%;
`;

/* The Simba card sizes to its content, so let it fill the column. */
const fillColumn = `
  display: flex;
  width: 100%;
  height: 100%;
  text-decoration: none;

  > * {
    flex: 1;
  }
`;

const PlainWrapper = styled.div`
  ${fillColumn}
`;

const StyledA = styled.a`
  ${fillColumn}

  color: ${nafColor.signature.black} !important;
`;

const StyledLink = styled(Link)`
  ${fillColumn}

  color: ${nafColor.signature.black} !important;
`;
